import { Component, Input, Output, EventEmitter, OnDestroy, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { VersionService, SolarData, SolarHistoryPoint, DayStat } from '../version.service';
import { formatW, formatKwh, updatedAgo as fmtAgo } from '../shared/format.utils';
import { CHART_RANGES, CHART_RANGE_MS, ChartRange, coveringRange, parseChartRange } from '../shared/chart.utils';
import { DeepLinkService } from '../shared/deep-link.service';
import { GRID_LIMIT_COLOR, PHASE_COLORS } from '../sparkline-chart/sparkline-chart.component';

/** The solar cards with a chart popup (view path 'energie/<mode>', also its expanded chart's link id). */
type SolarMode = 'pvYield' | 'battery' | 'grid' | 'consumption';
const SOLAR_MODES: readonly SolarMode[] = ['pvYield', 'battery', 'grid', 'consumption'];
/** Deep-link key of the tab's range. */
const RANGE_KEY = 'energie.range';

@Component({
  selector: 'app-energie',
  templateUrl: './energie.component.html',
  styles: [`
    .kpi-chart { display: block; margin-top: 6px; }
    .kpi-batt { font-size: .72rem; }
    .kpi-phases { font-size: .68rem; line-height: 1.6; }
    .kpi-autarky-sub { font-size: .68rem; line-height: 1.5; }
    .range-spin { display: none; }

    @media (max-width: 575.98px) {
      /* KPI card on a phone (full width): label left, value at the end of its row, the other lines below */
      .kpi-text {
        flex: 1 1 auto;
        display: grid;
        grid-template-columns: minmax(0, 1fr) auto;
        column-gap: .75rem;
        align-items: baseline;
      }
      .kpi-text > * { grid-column: 1 / -1; }
      .kpi-text > .kpi-label { grid-row: 1; grid-column: 1; }
      .kpi-text > .card-value { grid-row: 1; grid-column: 2; font-size: 1.25rem !important; line-height: 1.25; }
      .kpi-sub { font-size: .75rem; line-height: 1.4; }
      .kpi-phases { font-size: .75rem; }
      .kpi-chart { margin-top: 8px; }
      /* a loading range: its (pressed) button shows a spinner in place of the label, the button keeps its size */
      .range-busy > .range-label { opacity: 0; }
      .range-spin { display: block; position: absolute; inset: 0; margin: auto; width: 1rem; height: 1rem; border-width: .15em; }
      /* card popup: the chart fills the sheet's free height (styles.css dash-sheet-fill / -item) */
      .solar-sheet { height: 100%; }
    }
  `],
})
export class EnergieComponent implements OnInit, OnDestroy {
  @Input() solar:           SolarData | null        = null;
  @Input() solarHistory:    SolarHistoryPoint[]     = [];
  @Input() solarFlash       = false;
  @Input() liveDotOn        = true;
  @Input() solarUpdatedAt:  number | null           = null;
  @Input() nowTick          = Date.now();
  @Input() todayStat:       DayStat | null          = null;
  @Input() darkMode         = false;

  @Output() openSolarChart  = new EventEmitter<void>();

  constructor(readonly translate: TranslateService, readonly vs: VersionService, private readonly link: DeepLinkService) {}

  private linkSub?: Subscription;

  readonly Math = Math;
  formatW   = formatW;
  formatKwh = formatKwh;

  // Per-tab time range: 'day' uses the live 24h history; longer ranges fetch the
  // downsampled long-term archive and drive every solar chart on this tab.
  range: ChartRange = 'day';
  rangeLoading = false;
  solarRangeHistory: SolarHistoryPoint[] = [];
  /** The range whose points the charts show: while another range's archive loads (or after it failed) the
   *  previous points stay, plotted in the new range's window, so the cards keep their charts and height. */
  private dataRange: ChartRange = 'day';
  private rangeReq = 0;   // drops archive responses of a range that was switched away from
  /** Range buttons; labels are i18n keys (RANGE_DAY …). */
  readonly chartRanges = CHART_RANGES;

  ngOnInit(): void {
    this.applyLink();
    this.linkSub = this.link.external$.subscribe(() => this.applyLink());
  }

  ngOnDestroy(): void {
    this.linkSub?.unsubscribe();
  }

  /** View path of a card's chart popup — also the link id of the expanded chart inside it. */
  popupPath(mode: SolarMode): string {
    return 'energie/' + mode;
  }

  /** Analyse-board chart id of a card popup's chart ("solar.battery"). */
  boardChart(mode: SolarMode): string {
    return 'solar.' + mode;
  }

  /** The open card popup's mode according to the link, else null. */
  private linkedPopup(): SolarMode | null {
    const mode = this.link.childOf('energie');
    return SOLAR_MODES.includes(mode as SolarMode) ? mode as SolarMode : null;
  }

  /** Range from the link (on init and on back / forward), widened so a linked zoom window of the open
   *  card popup lies inside the loaded data. */
  private applyLink(): void {
    const linked = parseChartRange(this.link.get(RANGE_KEY)) ?? 'day';
    const popup = this.linkedPopup();
    const range = coveringRange(linked, popup ? this.link.zoom(this.popupPath(popup)) : null);
    if (range !== linked) this.link.set(RANGE_KEY, range === 'day' ? null : range);
    if (range !== this.range) this.setRange(range);
  }

  /** A range button: into the link; the open popup's zoom window belonged to the old range. */
  selectRange(range: ChartRange): void {
    this.link.set(RANGE_KEY, range === 'day' ? null : range);
    const popup = this.linkedPopup();
    if (popup) this.link.setZoom(this.popupPath(popup), null);
    this.setRange(range);
  }

  get displaySolar(): SolarHistoryPoint[] {
    return this.dataRange === 'day' ? this.solarHistory : this.solarRangeHistory;
  }

  /** Translated label of the current range (re-read every change detection, so it follows a language switch). */
  get rangeLabel(): string {
    const key = this.chartRanges.find(r => r.key === this.range)?.label;
    return key ? this.translate.instant(key) : '';
  }

  get windowMs(): number {
    return CHART_RANGE_MS[this.range];
  }

  setRange(range: ChartRange): void {
    this.range = range;
    const req = ++this.rangeReq;
    if (range === 'day') { this.dataRange = 'day'; this.rangeLoading = false; return; }   // live history covers 24h
    const to   = Date.now();
    const from = to - CHART_RANGE_MS[range];
    this.rangeLoading = true;
    fetch(`/api/history?series=solar&from=${from}&to=${to}&buckets=600`)
      .then(r => r.json())
      .then((pts: any[]) => {
        if (req !== this.rangeReq) return;
        this.solarRangeHistory = pts ?? [];
        this.dataRange = range;
      })
      .catch(() => {})
      .finally(() => { if (req === this.rangeReq) this.rangeLoading = false; });
  }

  private readonly BATTERY_BARRIER_W = 7360;
  private readonly GRID_BARRIER_W    = 3680;
  /** Marker of the 16 A limit on the phase bars, in the colour of its line in the Verbrauch chart. */
  readonly gridLimitColor = GRID_LIMIT_COLOR;

  hasPhaseData(solar: SolarData): boolean {
    return solar.consumptionL1W > 0 || solar.consumptionL2W > 0 || solar.consumptionL3W > 0;
  }

  phaseData(solar: SolarData): {label: string; watts: number; pct: number; gridBarrierPct: number; color: string}[] {
    const phases = [
      { label: 'L1', watts: solar.consumptionL1W },
      { label: 'L2', watts: solar.consumptionL2W },
      { label: 'L3', watts: solar.consumptionL3W },
    ];
    const max = this.BATTERY_BARRIER_W;
    return phases.map((p, i) => ({
      ...p,
      pct: Math.min(100, p.watts / max * 100),
      gridBarrierPct: this.GRID_BARRIER_W / max * 100,
      color: PHASE_COLORS[i],
    }));
  }

  solarMinMax(metric: 'pvYield' | 'battery' | 'grid' | 'consumption'): { min: number; max: number } {
    if (!this.displaySolar.length) return { min: 0, max: 0 };
    const values = this.displaySolar.map(p => {
      switch (metric) {
        case 'pvYield':     return p.pvYieldW;
        case 'battery':     return -p.batteryDischargeW;
        case 'grid':        return p.gridConsumptionW;
        case 'consumption': return p.totalConsumptionW;
        default:            return 0;
      }
    });
    return { min: Math.min(...values), max: Math.max(...values) };
  }

  updatedAgo(ts: number | null): string { return fmtAgo(ts, this.nowTick); }

  batteryRuntime(solar: SolarData): string {
    if (!solar.batteryCapacityWh) return '';
    if (solar.batteryDischargeW > 0) {
      const hours = (solar.batteryPercent / 100 * solar.batteryCapacityWh) / solar.batteryDischargeW;
      const label = this.translate.instant('AKKU_REST');
      if (hours < 1)  return `${label} ≈ < 1 h`;
      if (hours < 24) return `${label} ≈ ${Math.round(hours)} h`;
      const days = Math.round(hours / 24);
      return `${label} ≈ ${days} ${days === 1 ? this.translate.instant('AKKU_TAG') : this.translate.instant('AKKU_TAGE')}`;
    }
    if (solar.batteryDischargeW < 0) {
      const chargeW     = -solar.batteryDischargeW;
      const remainingWh = (100 - solar.batteryPercent) / 100 * solar.batteryCapacityWh;
      const hours       = remainingWh / chargeW;
      const label       = this.translate.instant('AKKU_VOLL');
      if (hours < 1/60) return `${label} ≈ < 1 min`;
      if (hours < 1)    return `${label} ≈ ${Math.round(hours * 60)} min`;
      return `${label} ≈ ${Math.round(hours)} h`;
    }
    return '';
  }
}
